import type { BoardCellOutcome } from '@multirx/shared/game/boardGame.js';
import type { MeetingStatTarget } from '@multirx/shared/types/board.js';
import { CHAMPIONSHIP_STAR_MAX } from '@multirx/shared/game/starLadder.js';
import { renderStarsCar } from '../components/starRating.js';

/** Libellés alignés garage : « Stat (bonus) ». */
export const STAT_LABELS: Record<MeetingStatTarget, string> = {
  pilotage: 'Pilotage (virage)',
  defense: 'Défense (combativité)',
  engine: 'Moteur (ligne droite)',
  tire: 'Pneu (départ)',
  jokerLap: 'Joker (tour joker)',
};

/** Formulation naturelle dans la phrase d’intro (avec article). */
const STAT_IMPROVE_PHRASE: Record<MeetingStatTarget, string> = {
  pilotage: 'le pilotage (bonus de virage)',
  defense: 'la défense (bonus de combativité)',
  engine: 'le moteur (bonus de ligne droite)',
  tire: 'les pneus (bonus de départ)',
  jokerLap: 'le joker (bonus de tour joker)',
};

export type BoardDecisionCopy = {
  title: string;
  paragraphs: string[];
  confirmLabel: string;
  cancelLabel: string;
  /** false → bouton Payer désactivé (solde insuffisant). */
  canAfford: boolean;
  /** Modale d’info seule (bonus/malus points) — un bouton Continuer, pas d’annuler. */
  ackOnly?: boolean;
  /** Aperçu étoiles si la case touche une stat. */
  preview: {
    label: string;
    currentStars: number | null;
    delta: number;
    scopeHint: string;
  } | null;
};

function racePointsCost(n: number): string {
  return `${n} point${n > 1 ? 's' : ''} de course`;
}

function ownerPhrase(target: MeetingStatTarget): 'votre voiture' | 'votre pilote' | null {
  if (target === 'engine' || target === 'tire') return 'votre voiture';
  if (target === 'pilotage' || target === 'defense' || target === 'jokerLap') return 'votre pilote';
  return null;
}

function improveLead(target: MeetingStatTarget, permanent: boolean): string {
  const scope = permanent ? 'de façon permanente' : 'pour ce meeting';
  const owner = ownerPhrase(target);
  if (target === 'jokerLap') {
    return permanent
      ? 'Vous pouvez améliorer le joker (bonus de tour joker) de votre pilote pour toute la saison !'
      : 'Vous pouvez améliorer le joker (bonus de tour joker) de votre pilote pour ce meeting !';
  }
  return `Vous pouvez améliorer ${STAT_IMPROVE_PHRASE[target]} de ${owner} ${scope} !`;
}

function malusLead(target: MeetingStatTarget): string {
  const owner = ownerPhrase(target);
  return `Un malus menace ${STAT_IMPROVE_PHRASE[target]} de ${owner} pour ce meeting !`;
}

function payOrRefuseLine(cost: number, bonusDelta: number): string {
  return `Si vous payez ${racePointsCost(cost)}, vous gagnez un bonus de +${bonusDelta} jusqu’à la fin du meeting, sinon refusez et rien ne changera.`;
}

/** Texte + aperçu de stats pour une décision plateau (hors casino). */
export function buildBoardDecisionCopy(outcome: BoardCellOutcome): BoardDecisionCopy | null {
  switch (outcome.type) {
    case 'upgradeMeeting':
      return {
        title: 'Amélioration de meeting',
        paragraphs: [improveLead(outcome.target, false), payOrRefuseLine(outcome.cost, 1)],
        confirmLabel: 'Payer',
        cancelLabel: 'Refuser',
        canAfford: outcome.affordable,
        preview: {
          label: STAT_LABELS[outcome.target],
          currentStars: outcome.currentStars,
          delta: outcome.delta,
          scopeHint: 'Bonus temporaire (ce meeting)',
        },
      };
    case 'malusMeeting':
      return {
        title: 'Malus de meeting',
        paragraphs: [
          malusLead(outcome.target),
          `Payez ${racePointsCost(outcome.costToAvoid)} pour l’éviter, sinon un malus de −1 s’applique jusqu’à la fin du meeting.`,
        ],
        confirmLabel: 'Payer pour éviter',
        cancelLabel: 'Laisser le malus s’appliquer',
        canAfford: outcome.canAvoid,
        preview: {
          label: STAT_LABELS[outcome.target],
          currentStars: outcome.currentStars,
          delta: outcome.delta,
          scopeHint: 'Malus temporaire (ce meeting)',
        },
      };
    case 'upgradeCarPermanent': {
      if (!outcome.target) return null;
      const label = STAT_LABELS[outcome.target];
      return {
        title: 'Amélioration voiture permanente',
        paragraphs: [
          improveLead(outcome.target, true),
          `Si vous payez ${racePointsCost(outcome.cost)}, la stat monte d’une étoile pour de bon, sinon refusez et rien ne changera.`,
        ],
        confirmLabel: 'Payer',
        cancelLabel: 'Refuser',
        canAfford: outcome.affordable,
        preview: {
          label,
          currentStars: outcome.currentStars,
          delta: outcome.delta,
          scopeHint: 'Amélioration permanente',
        },
      };
    }
    case 'upgradeDriverPermanent': {
      if (!outcome.target) return null;
      const label = STAT_LABELS[outcome.target];
      return {
        title: 'Amélioration pilote permanente',
        paragraphs: [
          improveLead(outcome.target, true),
          `Si vous payez ${racePointsCost(outcome.cost)}, la stat monte d’une étoile pour de bon, sinon refusez et rien ne changera.`,
        ],
        confirmLabel: 'Payer',
        cancelLabel: 'Refuser',
        canAfford: outcome.affordable,
        preview: {
          label,
          currentStars: outcome.currentStars,
          delta: outcome.delta,
          scopeHint: 'Amélioration permanente',
        },
      };
    }
    case 'jokerSeasonUpgrade':
      return {
        title: 'Bonus Joker saisonnier',
        paragraphs: [
          improveLead('jokerLap', true),
          `Si vous payez ${racePointsCost(outcome.cost)}, le joker monte d’une étoile pour toute la saison, sinon refusez et rien ne changera.`,
        ],
        confirmLabel: 'Payer',
        cancelLabel: 'Refuser',
        canAfford: outcome.affordable,
        preview: {
          label: STAT_LABELS.jokerLap,
          currentStars: outcome.currentStars,
          delta: outcome.delta,
          scopeHint: 'Amélioration saisonnière (pilote)',
        },
      };
    case 'jokerMeetingUpgrade':
      return {
        title: 'Bonus Joker de meeting',
        paragraphs: [improveLead('jokerLap', false), payOrRefuseLine(outcome.cost, 1)],
        confirmLabel: 'Payer',
        cancelLabel: 'Refuser',
        canAfford: outcome.affordable,
        preview: {
          label: STAT_LABELS.jokerLap,
          currentStars: outcome.currentStars,
          delta: outcome.delta,
          scopeHint: 'Bonus temporaire (ce meeting)',
        },
      };
    case 'carPurchase':
      return {
        title: 'Achat de voiture',
        paragraphs: [
          `Pour ${racePointsCost(outcome.cost)}, le plateau tente de vous attribuer une voiture au moins aussi bonne que la vôtre (même division), si une place libre existe.`,
          'Si aucune voiture n’est disponible, vos points de course ne sont pas débités.',
        ],
        confirmLabel: 'Payer',
        cancelLabel: 'Refuser',
        canAfford: outcome.affordable,
        preview: null,
      };
    case 'bonusPoints':
      return {
        title: 'Bonus de points',
        paragraphs: [
          `Case bonus ! Vous gagnez ${racePointsCost(outcome.amount)}.`,
          'Ces points de course servent à payer les améliorations et le casino sur le plateau — ce ne sont pas des points de classement.',
        ],
        confirmLabel: 'Continuer',
        cancelLabel: '',
        canAfford: true,
        ackOnly: true,
        preview: null,
      };
    case 'malusPoints':
      return {
        title: 'Malus de points',
        paragraphs: [
          outcome.applied > 0
            ? `Case malus ! Vous perdez ${racePointsCost(outcome.applied)}.`
            : `Case malus de ${racePointsCost(outcome.amount)} — votre solde est insuffisant, aucun point n’est retiré.`,
          'Ce n’est pas un malus de classement : uniquement vos points de course du plateau.',
        ],
        confirmLabel: 'Continuer',
        cancelLabel: '',
        canAfford: true,
        ackOnly: true,
        preview: null,
      };
    default:
      return null;
  }
}

/** HTML d’aperçu : étoiles actuelles → étoiles après effet, delta en vert/rouge. */
export function boardStatPreviewHtml(preview: NonNullable<BoardDecisionCopy['preview']>): string {
  const tone = preview.delta >= 0 ? 'bonus' : 'malus';
  const deltaText = preview.delta >= 0 ? `+${preview.delta}` : String(preview.delta);
  const starsBlock =
    preview.currentStars == null
      ? '<span class="board-stat-preview__no-stars">Pas d’échelle d’étoiles</span>'
      : (() => {
          const from = preview.currentStars;
          const to = Math.max(1, Math.min(CHAMPIONSHIP_STAR_MAX, from + preview.delta));
          return `<span class="board-stat-preview__stars-from" aria-label="${from}/5">${renderStarsCar(from)}</span>
            <span class="board-stat-preview__arrow" aria-hidden="true">→</span>
            <span class="board-stat-preview__stars-to board-stat-preview__stars-to--${tone}" aria-label="${to}/5">${renderStarsCar(to)}</span>`;
        })();

  return `<div class="board-stat-preview board-stat-preview--${tone}">
    <div class="board-stat-preview__head">
      <span class="board-stat-preview__label">${escapeHtml(preview.label)}</span>
      <span class="board-stat-preview__delta">${deltaText}</span>
    </div>
    <div class="board-stat-preview__stars">${starsBlock}</div>
    <p class="board-stat-preview__scope">${escapeHtml(preview.scopeHint)}</p>
  </div>`;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
